import { LoaderFunctionArgs, redirect } from "react-router-dom";

import { dashboardTournamentsControllerOverview } from "@/api/__generated__/dashboard-tournaments/dashboard-tournaments";
// Deep path, not the `@/utils/common` barrel: a route loader runs before the app
// tree and its test mocks only `@/env` + the generated endpoint, so pulling the
// whole barrel (imageKit, file utils, …) in would add graph for nothing. The
// sibling route modules import from `@/utils/common/*` the same way.
import { getTournamentManagerHref } from "@/utils/common/getTournamentUrl";

const withLocationSuffix = (pathname: string, requestUrl: string): string => {
  const url = new URL(requestUrl);
  return `${pathname}${url.search}${url.hash}`;
};

/**
 * Resolves the tournament's Manager link, or `null` when it cannot be built.
 *
 * A loader only sees the route param — the public `trn_…` id — and Manager deep
 * links are numeric-only: a public id there does not 404, it silently opens a
 * *different* tournament (see {@link getTournamentManagerHref}). So the numeric
 * `internalId` is fetched first, straight through the generated endpoint
 * function (a loader cannot use a React Query hook; the function shares the same
 * authenticated axios client).
 *
 * Any failure — request error, 403/404, or a payload without a usable
 * `internalId` — returns `null` so the caller can fall back to an Admin page
 * that always works. This trades a round-trip on the way in for never landing
 * the user on the wrong tournament.
 */
const resolveTournamentManagerHref = async (tournamentId: string): Promise<string | null> => {
  try {
    const { internalId } = await dashboardTournamentsControllerOverview(tournamentId);

    return Number.isInteger(internalId) ? getTournamentManagerHref(internalId) : null;
  } catch {
    return null;
  }
};

/** The tournament's Admin matches page, in whatever scope the URL already carries. */
const matchesFallback = (requestUrl: string): string => {
  const pathname = `${new URL(requestUrl).pathname.replace(/\/$/, "")}/matches`;

  return withLocationSuffix(pathname, requestUrl);
};

/**
 * The tournament's own page lives in the **Manager** app — Admin owns only its
 * sub-pages (matches, entries, results, transactions) — so a canonical
 * tournament root hands off to Manager.
 *
 * Unlike a clicked tournament link, this navigates the **current** tab: someone
 * arriving directly at the URL has no originating tab to preserve.
 * {@link getTournamentManagerHref} returns the same-app
 * `/manager-redirect?to=…` hop, which mints the handoff token and forwards.
 *
 * Whenever the numeric id cannot be resolved, this falls back to the
 * tournament's Admin **matches** page rather than emitting a Manager URL built
 * from the public id.
 */
export const redirectTournamentRootToManager = async ({ params, request }: LoaderFunctionArgs) => {
  const tournamentId = params.tournamentId;

  if (!tournamentId) return redirect("/tournaments");

  const managerHref = await resolveTournamentManagerHref(tournamentId);

  return redirect(managerHref ?? matchesFallback(request.url));
};

/**
 * Keep previously shared `/tournaments/:id[/section]` links working while all
 * newly generated links use the singular tournament detail namespace.
 *
 * An explicit section still resolves to its Admin page (scope prefix, search and
 * hash preserved). With no section the link meant the tournament itself, so it
 * follows {@link redirectTournamentRootToManager} into Manager — and falls back
 * to the same `matches` section when the numeric id cannot be resolved.
 */
export const redirectLegacyTournamentDetail = async ({ params, request }: LoaderFunctionArgs) => {
  const tournamentId = params.tournamentId;
  const requestedSection = params["*"]?.replace(/^\/+|\/+$/g, "") || null;

  if (!tournamentId) return redirect("/tournaments");

  if (!requestedSection) {
    const managerHref = await resolveTournamentManagerHref(tournamentId);

    if (managerHref) return redirect(managerHref);
  }

  const url = new URL(request.url);
  const pathname = url.pathname.replace(
    /\/tournaments\/[^/]+(?:\/.*)?$/,
    `/tournament/${tournamentId}/${requestedSection ?? "matches"}`
  );

  return redirect(withLocationSuffix(pathname, request.url));
};
